import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import SkillModal from './SkillModal';
import MyDataTable from '../../../components/MyDataTable';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import CustomButton from '../../../components/CustomButton';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { HasOpenVacancies } from '../../../services/central-api/deac';
import { accessService } from '../../../services/central-api/access';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function Skills({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'Skills'>>();
    const { user: authenticatedUser } = useAuth();

    // filters
    const [filters, setFilters] = useFilters<AccessParamList['Skills']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [skills, setSkills] = useState<Skill[]>([]);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedSkill, setSelectedSkill] = useState<Skill>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessSkills', filters],
        queryFn: () => accessService.getSkills(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setSkills(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(skill: Skill) {
        try {
            await accessService.deleteSkill(skill.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            } else if (err instanceof HasOpenVacancies) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('skillHasOpenVacancies'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    return <Container>
        <Header
            title={translate('skills')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setSelectedSkill(undefined);
                setModalVisible(true);
            }} />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredSkills')}
                zIndex={2}
                filter={<Filter
                    authenticatedUser={authenticatedUser}
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name,
                        grow: 5
                    },
                    {
                        name: translate('userCount'),
                        selector: row => row.userCount || 0,
                    },
                    {
                        name: translate('actingBody'),
                        selector: row => row.actingBody.name,
                        wrap: true,
                    },
                    {
                        button: true,
                        cell: row =>
                            <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedSkill(row);
                                        setModalVisible(true);
                                    }
                                }, {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedSkill(row);
                                        setDeleteModalVisible(true);
                                    },
                                }]}
                            />
                    }]}
                    data={skills}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>
        {/* Delete Modal */}
        <DeleteModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={String(selectedSkill?.name) || ''}
            onSubmit={async () => {
                try {
                    if (selectedSkill) {
                        await confirmDelete(selectedSkill);
                    }
                } catch (err) {
                    if (err instanceof ClientError) {
                        return Toast.show({
                            type: 'sentinelxError',
                            text1: translate(err.message),
                        });
                    }

                    console.error(err);
                    Toast.show({
                        type: 'sentinelxError',
                        text1: translate('unexpectedError'),
                    });
                }
            }}
            onCancel={() => {
                setSelectedSkill(undefined);
            }}
        />
        <SkillModal
            isModalVisible={isModalVisible}
            authenticatedUser={authenticatedUser}
            skillId={selectedSkill?.id}
            onClose={() => {
                setSelectedSkill(undefined);
                setModalVisible(false);
                refetch();
            }}
        />
    </Container>;
}
